// Constants
const WISHLIST_LOADED_EVENT = 'wishlist-loaded';
const WISHLIST_UPDATED_EVENT = 'wishlist-updated';
const WISHLIST_ITEM_REMOVED_EVENT = 'wishlist-item-removed';

// Shared wishlist state across all instances
let sharedWishlistData = {
  items: [],
  count: 0,
  loaded: false,
  loading: false, // prevent multiple simultaneous loads
  _itemsSet: null, // cached Set for O(1) lookup
  _itemsSetDirty: true, // flag to rebuild Set when items change
};

/**
 * Main Wishlist Component - Acts as a wrapper
 * Multiple instances share the same wishlist state
 */
class ArnWishlist extends HTMLElement {
  constructor() {
    super();
    this.customerId = ShopifyAnalytics?.meta?.page?.customerId || null;

    // Inizializza l'oggetto globale per il mapping variant-UPC se non esiste
    if (!window.tealiumTrackingUtils?.mappingVariantIdsUpcs) {
      window.tealiumTrackingUtils = window.tealiumTrackingUtils || {};
      window.tealiumTrackingUtils.mappingVariantIdsUpcs = {};
    }
  }

  connectedCallback() {
    // Check if this is a wishlist page instance
    const isWishlistPage = this.dataset.page === 'wishlist';

    // Only load wishlist data once for the first instance
    if (!sharedWishlistData.loaded && !sharedWishlistData.loading) {
      sharedWishlistData.loading = true; // set loading flag before fetch
      // [debug] console.log('[ARN Wishlist] First instance, loading wishlist data...');
      this.loadWishlist();
    } else if (sharedWishlistData.loaded && isWishlistPage) {
      // [debug] console.log('[ARN Wishlist] Wishlist page, loading items...');
      this.loadWishlistPage();
    } else if (sharedWishlistData.loading) {
      // [debug] console.log('[ARN Wishlist] Already loading, waiting for loaded event...');
      // Wait for loading to finish
      document.addEventListener(WISHLIST_LOADED_EVENT, () => {
        if (isWishlistPage) {
          this.loadWishlistPage();
        }
      }, { once: true });
    } else {
      // [debug] console.log('[ARN Wishlist] Using already loaded wishlist data');
    }

    // If this is the wishlist page, listen for item removed events
    if (isWishlistPage) {
      document.addEventListener(WISHLIST_ITEM_REMOVED_EVENT, (e) => {
        this.removeItemFromPage(e.detail.productId, e.detail.variantId);
      });
    }
  }

  /**
   * Get wishlist ID from cookies (for guest users)
   */
  getWishlistId() {
    const cookies = decodeURIComponent(document.cookie).split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf('wishlist_id=') === 0) {
        return cookie.substring('wishlist_id='.length);
      }
    }

    return null;
  }

  /**
   * Aggiorna il mapping globale variant-UPC
   * @param {string} variantId - ID della variante
   * @param {string} upc - Codice UPC/barcode
   */
  updateVariantUpcMapping(variantId, upc) {
    if (variantId && upc) {
      window.tealiumTrackingUtils.mappingVariantIdsUpcs[String(variantId)] = String(upc);
      // [debug] console.log('[ARN Wishlist] Updated mapping:', variantId, '->', upc);
    }
  }

  /**
   * Rimuovi una variante dal mapping globale
   * @param {string} variantId - ID della variante da rimuovere
   */
  removeVariantFromMapping(variantId) {
    if (variantId && window.tealiumTrackingUtils?.mappingVariantIdsUpcs[String(variantId)]) {
      delete window.tealiumTrackingUtils.mappingVariantIdsUpcs[String(variantId)];
      // [debug] console.log('[ARN Wishlist] Removed from mapping:', variantId);
    }
  }

  /**
   * Ricostruisce completamente il mapping dalla wishlist corrente
   */
  rebuildVariantUpcMapping() {
    window.tealiumTrackingUtils.mappingVariantIdsUpcs = {};

    if (Array.isArray(sharedWishlistData.items)) {
      sharedWishlistData.items.forEach(item => {
        if (item.variant_id && item.variant_sku) {
          window.tealiumTrackingUtils.mappingVariantIdsUpcs[String(item.variant_id)] = String(item.variant_sku);
          // [debug] console.log('[ARN Wishlist] Rebuilt mapping:', item.variant_id, '->', item.variant_sku);
        }
      });
    }
  }

  /**
   * Load wishlist data from Smart Wishlist API
   */
  loadWishlist() {
    const wishlistId = this.getWishlistId();
    let urlToFetch;

    // If no wishlist ID exists and user is not logged in, initialize empty wishlist
    if (!wishlistId && !this.customerId) {
      // [debug] console.log('[ARN Wishlist] No wishlist ID found, initializing empty wishlist');
      sharedWishlistData = {
        items: [],
        count: 0,
        loaded: true,
        loading: false,
        _itemsSet: new Set(),
        _itemsSetDirty: false
      };

      // Svuota anche il mapping
      window.tealiumTrackingUtils.mappingVariantIdsUpcs = {};

      // Dispatch loaded event immediately
      setTimeout(() => {
        const event = new CustomEvent(WISHLIST_LOADED_EVENT, {
          detail: sharedWishlistData
        });
        document.dispatchEvent(event);

        // If this is the wishlist page, show empty state
        if (this.dataset.page === 'wishlist') {
          this.loadWishlistPage();
        }
      }, 100);

      return;
    }

    if (this.customerId) {
      urlToFetch = `${Shopify.routes.root}a/wishlist?type=api&customerid=${this.customerId}&version=1`;
    } else {
      urlToFetch = `${Shopify.routes.root}a/wishlist?type=api&wishlistid=${wishlistId}&version=1`;
    }

    // [debug] console.log('[ARN Wishlist] Fetching wishlist from:', urlToFetch);

    fetch(urlToFetch)
      .then(response => response.text())
      .then((data) => {
        const parsedData = JSON.parse(data);

        // Check if the response is an error
        if (parsedData.type === 'error') {
          console.warn('[ARN Wishlist] API returned error:', parsedData);
          // Initialize with empty wishlist
          sharedWishlistData = {
            items: [],
            count: 0,
            loaded: true,
            loading: false,
            _itemsSet: new Set(),
            _itemsSetDirty: false
          };
          window.tealiumTrackingUtils.mappingVariantIdsUpcs = {};
        } else {
          sharedWishlistData = parsedData;
          sharedWishlistData.loaded = true;
          sharedWishlistData.loading = false;
          sharedWishlistData._itemsSetDirty = true; // Mark Set as dirty to rebuild on first check

          // Ricostruisci il mapping variant-UPC dalla risposta API
          this.rebuildVariantUpcMapping();
        }

        // [debug] console.log('[ARN Wishlist] Wishlist data loaded:', sharedWishlistData);

        // Wait for Smart Wishlist to be ready
        const startTime = new Date();
        const intervalId = setInterval(() => {
          if (typeof window.SmartWishlistMain === 'function') {
            clearInterval(intervalId);
            // [debug] console.log('[ARN Wishlist] Smart Wishlist ready, dispatching event');

            // Dispatch loaded event
            const event = new CustomEvent(WISHLIST_LOADED_EVENT, {
              detail: sharedWishlistData
            });
            document.dispatchEvent(event);

            // Update UI
            this.updateWishlistBubble();

            // If this is the wishlist page, load items
            if (this.dataset.page === 'wishlist') {
              this.loadWishlistPage();
            }
          }

          // Stop polling after 30 seconds
          if (new Date() - startTime > 30000) {
            clearInterval(intervalId);
            console.warn('[ARN Wishlist] Timeout waiting for Smart Wishlist');
          }
        }, 100);
      })
      .catch(err => {
        console.error('[ARN Wishlist] Error loading wishlist:', err);
        // Initialize with empty wishlist on error
        sharedWishlistData = {
          items: [],
          count: 0,
          loaded: true,
          loading: false,
          _itemsSet: new Set(),
          _itemsSetDirty: false
        };
        window.tealiumTrackingUtils.mappingVariantIdsUpcs = {};
      });
  }

  /**
   * Create a Set of wishlist items for O(1) lookup
   * @returns {Set<string>} Set of "productId-variantId" keys
   */
  getWishlistItemsSet() {
    if (!Array.isArray(sharedWishlistData.items)) return new Set();
    
    return new Set(
      sharedWishlistData.items.map(item => 
        `${String(item.id)}-${String(item.variant_id)}`
      )
    );
  }

  /**
   * Add item to wishlist
   */
  addItem(wishlistItem) {
    try {
      const { productId, variantId, upc, available, price, priceFull } = wishlistItem.dataset;
      if (!productId || !variantId) throw new Error('Missing product or variant ID');

      // [debug] console.log('[ARN Wishlist] Adding - Product:', productId, 'Variant:', variantId);

      if (typeof AddToSmartWishlist === 'function') {
        AddToSmartWishlist(productId, variantId, this.customerId);
      }

      sharedWishlistData.items.push({
        id: productId,
        variant_id: variantId,
        handle: wishlistItem.handle
      });

      sharedWishlistData.count = sharedWishlistData.items.length;
      sharedWishlistData._itemsSetDirty = true; // Invalidate cache

      const tealiumPubObj = {
        Events_ProdFavAdd: "1",
        Products: {
          [upc]: {
            Status: available ? "Available" : "Out-of-stock",
            Price: `${price}`,
            PriceFull: `${priceFull}`
          }
        }
      };

      publish(PUB_SUB_EVENTS.wishlistAdd, tealiumPubObj);
      
      this.updateWishlistBubble();
      this.dispatchUpdatedEvent();
    } catch (err) {
      console.error('[ARN Wishlist] Error adding item:', err);
    }
  }

  /**
   * Remove item from wishlist
   */
  removeItem(wishlistItem) {
    try {
      const { productId, variantId } = wishlistItem.dataset;
      if (!productId || !variantId) throw new Error('Missing product or variant ID');

      // [debug] console.log('[ARN Wishlist] Removing - Product:', productId, 'Variant:', variantId);

      if (typeof RemoveFromSmartWishlist === 'function') {
        RemoveFromSmartWishlist(productId, variantId);
      }

      sharedWishlistData.items = sharedWishlistData.items.filter(item =>
        !(String(item.id) === String(productId) && String(item.variant_id) === String(variantId))
      );

      sharedWishlistData.count = sharedWishlistData.items.length;
      sharedWishlistData._itemsSetDirty = true; // Invalidate cache

      this.updateWishlistBubble();

      // Dispatch event for notifying wishlist page to remove item from DOM
      const removeEvent = new CustomEvent(WISHLIST_ITEM_REMOVED_EVENT, {
        detail: { productId, variantId }
      });
      document.dispatchEvent(removeEvent);

      this.dispatchUpdatedEvent();
    } catch (err) {
      console.error('[ARN Wishlist] Error removing item:', err);
    }
  }

  /**
   * Check if item is in wishlist (optimized with Set for O(1) lookup)
   */
  isItemInWishlist(wishlistItem) {
    if (!Array.isArray(sharedWishlistData.items)) return false;

    const { productId, variantId } = wishlistItem.dataset;
    const key = `${String(productId)}-${String(variantId)}`;
    
    // Use cached Set if available, otherwise create it
    if (!sharedWishlistData._itemsSet || sharedWishlistData._itemsSetDirty) {
      sharedWishlistData._itemsSet = this.getWishlistItemsSet();
      sharedWishlistData._itemsSetDirty = false;
      // [debug] console.log('[ARN Wishlist] Rebuilt items Set for optimized lookup');
    }
    
    return sharedWishlistData._itemsSet.has(key);
  }

  /**
   * Toggle wishlist item
   */
  toggleWishlistItem(wishlistItem) {
    if (this.isItemInWishlist(wishlistItem)) {
      this.removeItem(wishlistItem);
    } else {
      this.addItem(wishlistItem);
    }
  }

  /**
   * Update wishlist bubble in header
   */
  updateWishlistBubble() {
    const bubble = document.getElementById('wishlist-bubble');
    const mobileDrawerBubble = document.getElementById('wishlist-mobile-drawer-bubble');
    
    if (bubble) {
      if (sharedWishlistData.count > 0) {
        bubble.classList.remove('tw-hidden');
        // Set a data attribute for the count without altering visible content for accessibility
        bubble.setAttribute('data-count', sharedWishlistData.count);
      } else {
        bubble.classList.add('tw-hidden');
        bubble.removeAttribute('data-count');
      }
    }

    if (mobileDrawerBubble) {
      if (sharedWishlistData.count > 0) {
        mobileDrawerBubble.classList.remove('tw-hidden');
        // Set a data attribute for the count without altering visible content for accessibility
        mobileDrawerBubble.setAttribute('data-count', sharedWishlistData.count);
      } else {
        mobileDrawerBubble.classList.add('tw-hidden');
        mobileDrawerBubble.removeAttribute('data-count');
      }
    }
  }

  /**
   * Dispatch updated event
   */
  dispatchUpdatedEvent() {
    const event = new CustomEvent(WISHLIST_UPDATED_EVENT, {
      detail: sharedWishlistData
    });
    document.dispatchEvent(event);
  }

  /**
   * Load wishlist page items
   * Fetches product cards using Section Rendering API in batches.
   *
   * Pattern 3 (async fetch card skeleton): once we know how many items the
   * wishlist has, we render N skeleton slots immediately, then progressively
   * replace each slot with the fetched card. The existing full-section logo
   * loader ("[data-wishlist-loader]") is used only during phase 1 (wishlist
   * API call in loadWishlist()); it is hidden here before the skeleton grid
   * becomes visible so the two loaders never overlap.
   */
  async loadWishlistPage() {
    // [debug] console.log('[ARN Wishlist Page] Loading wishlist items...');

    const loader = this.querySelector('[data-wishlist-loader]');
    const emptyWrapper = this.querySelector('#wishlist-empty-wrapper');
    const productsWrapper = this.querySelector('#wishlist-products-wrapper');
    const productsContainer = this.querySelector('#wishlist-products');
    const itemCount = this.querySelector('[data-wishlist-item-count]');
    const loginBannerTemplate = this.querySelector('#wishlist-login-banner-template');
    const skeletonSlotTemplate = this.querySelector('#wishlist-skeleton-slot-template');

    // Hide the phase-1 logo loader; from here on, the skeleton grid (or empty
    // state) is the only visual cue.
    if (loader) loader.style.display = 'none';

    // Check if wishlist is empty
    if (!sharedWishlistData.items || sharedWishlistData.items.length === 0) {
      if (emptyWrapper) emptyWrapper.classList.remove('tw-hidden');
      if (productsWrapper) productsWrapper.classList.add('tw-hidden');
      if (itemCount) itemCount.textContent = '(0)';
      // [debug] console.log('[ARN Wishlist Page] Wishlist is empty');
      return;
    }

    // Update count
    if (itemCount) {
      itemCount.textContent = `(${sharedWishlistData.count})`;
    }

    // Clear existing products
    if (productsContainer) {
      productsContainer.innerHTML = '';
    }

    const items = sharedWishlistData.items;

    // ---- Pattern 3: render skeleton slots up-front ----
    const slots = [];
    if (productsContainer && skeletonSlotTemplate) {
      for (let i = 0; i < items.length; i++) {
        const slotFragment = skeletonSlotTemplate.content.cloneNode(true);
        const slot = slotFragment.querySelector('.arn-card-fetch-slot');
        if (!slot) continue;
        slot.dataset.productId = items[i].id;
        slot.dataset.variantId = items[i].variant_id;
        productsContainer.appendChild(slotFragment);
        slots.push(productsContainer.lastElementChild);
      }
    }

    // ---- Login banner: insert upfront at the grid position used previously ----
    // Old behavior: inserted after the 2nd successfully loaded card (i.e. DOM
    // position 2). Inserting upfront makes the banner visible immediately for
    // guests while the skeletons resolve. Grid placement is driven by CSS.
    let loginBannerInserted = false;
    const ensureBannerStyle = () => {
      if (document.querySelector('#wishlist-banner-style')) return;
      const style = document.createElement('style');
      style.id = 'wishlist-banner-style';
      style.textContent = `
        @media (min-width: 768px) {
          .wishlist-login-banner {
            grid-column: 1 / span 2 !important;
            grid-row: 2 / span 2 !important;
          }
        }
        @media (min-width: 1024px) {
          .wishlist-login-banner {
            grid-column: 3 / span 2 !important;
            grid-row: 1 / span 2 !important;
          }
        }
      `;
      document.head.appendChild(style);
    };
    const insertLoginBanner = (beforeNode) => {
      if (loginBannerInserted || !loginBannerTemplate || !productsContainer) return;
      const bannerFragment = loginBannerTemplate.content.cloneNode(true);
      const bannerElement = bannerFragment.querySelector('[data-wishlist-login-banner]');
      if (!bannerElement) return; // e.g. logged-in user — template is empty
      bannerElement.style.cssText = `
        grid-column: 1 / -1;
        grid-row: auto;
      `;
      ensureBannerStyle();
      if (beforeNode && beforeNode.parentElement === productsContainer) {
        productsContainer.insertBefore(bannerFragment, beforeNode);
      } else {
        productsContainer.appendChild(bannerFragment);
      }
      loginBannerInserted = true;
    };

    if (slots.length >= 3) {
      // Banner between 2nd and 3rd slot — matches the previous visual layout.
      insertLoginBanner(slots[2]);
    } else {
      insertLoginBanner(null);
    }

    // Reveal the products grid with the skeleton slots already in place.
    if (emptyWrapper) emptyWrapper.classList.add('tw-hidden');
    if (productsWrapper) productsWrapper.classList.remove('tw-hidden');

    try {
      // Fetch products in batches of 10
      const BATCH_SIZE = 10;

      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        // [debug] console.log(`[ARN Wishlist Page] Fetching batch ${Math.floor(i / BATCH_SIZE) + 1} (${batch.length} items)`);

        const fetchArray = batch.map(item => {
          // Get settings from the main section
          const showPrice = this.dataset.cardShowPrice === 'true';
          const showVat = this.dataset.showVatIncludedLabel === 'true';
          const url = `${Shopify.routes.root}products/${item.handle}/?sections=arn-wishlist-item&variant=${item.variant_id}&card_show_price=${showPrice}&show_vat_included_label=${showVat}`;
          return fetch(url);
        });

        const responses = await Promise.all(fetchArray);
        const jsonResponses = await Promise.all(responses.map(res => res.json()));

        // Process each product in the batch — replace its skeleton slot in-place.
        jsonResponses.forEach((response, batchIndex) => {
          const globalIndex = i + batchIndex;
          const item = items[globalIndex];
          const targetSlot = slots[globalIndex];
          const htmlString = response['arn-wishlist-item'];

          if (!htmlString) {
            console.warn('[ARN Wishlist Page] No HTML returned for item:', item);
            if (targetSlot) {
              targetSlot.dataset.cardFetchState = 'error';
              targetSlot.remove();
            }
            return;
          }

          const html = new DOMParser().parseFromString(htmlString, 'text/html');
          const productCard = html.querySelector('.arn-product-card');

          if (productCard && targetSlot) {
            // Promote the skeleton slot to the real "wishlist-page-item" <li>.
            targetSlot.className = 'wishlist-page-item';
            targetSlot.dataset.cardFetchState = 'loaded';
            targetSlot.dataset.productId = item.id;
            targetSlot.dataset.variantId = item.variant_id;
            targetSlot.replaceChildren(productCard);
          } else {
            console.warn('[ARN Wishlist Page] Product card not found for:', item);
            if (targetSlot) {
              targetSlot.dataset.cardFetchState = 'error';
              targetSlot.remove();
            }
          }
        });
      }

      // [debug] console.log('[ARN Wishlist Page] All items loaded successfully');

      // Initialize video controls for dynamically loaded content
      setTimeout(() => {
        if (typeof window.initVideoControls === 'function') {
          window.initVideoControls();
          // [debug] console.log('[ARN Wishlist Page] Video controls initialized');
        }
      }, 100);

      // Initialize add to cart functionality for wishlist items
      setTimeout(() => {
        initWishlistAddToCart();
        // [debug] console.log('[ARN Wishlist Page] Add to cart initialized');
      }, 200);

    } catch (error) {
      console.error('[ARN Wishlist Page] Error loading items:', error);
      if (loader) loader.style.display = 'none';
    }
  }

  /**
   * Remove item from wishlist page DOM
   */
  removeItemFromPage(productId, variantId) {
    if (this.dataset.page !== 'wishlist') return;

    const container = this.querySelector('#wishlist-products');
    if (!container) {
      console.warn('[ARN Wishlist Page] Products container not found');
      return;
    }

    // [debug] console.log('[ARN Wishlist Page] Trying to remove from DOM - Product:', productId, 'Variant:', variantId);

    // Search through all items manually using dataset
    const allItems = container.querySelectorAll('li.wishlist-page-item');
    let item = null;

    for (const el of allItems) {
      if (String(el.dataset.productId) === String(productId) &&
        String(el.dataset.variantId) === String(variantId)) {
        item = el;
        break;
      }
    }

    if (item) {
      item.remove();
      // [debug] console.log('[ARN Wishlist Page] Item removed from DOM');

      // Update count
      const itemCount = this.querySelector('[data-wishlist-item-count]');
      if (itemCount) {
        itemCount.textContent = `(${sharedWishlistData.count})`;
      }

      // Check if wishlist is now empty
      if (sharedWishlistData.count === 0) {
        const emptyWrapper = this.querySelector('#wishlist-empty-wrapper');
        const productsWrapper = this.querySelector('#wishlist-products-wrapper');
        if (emptyWrapper) emptyWrapper.classList.remove('tw-hidden');
        if (productsWrapper) productsWrapper.classList.add('tw-hidden');
      }
    } else {
      console.warn('[ARN Wishlist Page] Could not find item to remove - Product:', productId, 'Variant:', variantId);
    }
  }
}

customElements.define('arn-wishlist', ArnWishlist);

/**
 * Wishlist Toggle Button Component
 * Simplified - one button per product, managed by slider
 */
class ArnWishlistToggleButton extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.product_id;
    this.variantId = this.dataset.variant_id;
    this.buttonEl  = this.querySelector('button');

    // [debug] console.log('[ARN Wishlist Button] Created - Product:', this.productId, 'Variant:', this.variantId);
  }

  connectedCallback() {
    // Get parent wishlist component
    this.wishlist = this.closest('arn-wishlist');

    if (!this.wishlist) {
      console.warn('[ARN Wishlist Button] No parent arn-wishlist found');
      return;
    }

    if (!this.productId || !this.variantId) {
      console.warn('[ARN Wishlist Button] Missing product or variant ID');
      return;
    }

    // Check if wishlist is already loaded
    if (sharedWishlistData.loaded) {
      // [debug] console.log('[ARN Wishlist Button] Wishlist already loaded, enabling immediately');
      this.updateUI();
    } else {
      // Disable button until wishlist is loaded
      this.setAttribute('disabled', 'true');
      this.setAttribute('aria-busy', 'true');

      // Listen for wishlist loaded event
      document.addEventListener(WISHLIST_LOADED_EVENT, () => {
        this.removeAttribute('disabled');
        this.removeAttribute('aria-busy');
        this.updateUI();
        // [debug] console.log('[ARN Wishlist Button] Enabled');
      }, { once: true });
    }

    // Handle click
    this.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Don't do anything if disabled
      if (this.hasAttribute('disabled')) {
        console.warn('[ARN Wishlist Button] Click ignored - button disabled');
        return;
      }

      // [debug] console.log('[ARN Wishlist Button] Clicked');

      const wishlistItem = {
        dataset: {
          productId: this.productId,
          variantId: this.variantId,
          upc: this.dataset.upc,
          available: this.dataset.available,
          properties: this.dataset.properties,
          price: this.dataset.price,
          priceFull: this.dataset.priceFull
        },
        handle: this.dataset.handle
      };

      this.wishlist.toggleWishlistItem(wishlistItem);
    });

    // Keyboard accessibility
    this.addEventListener('keydown', (event) => {
      if (document.activeElement !== this) return;
      if (this.hasAttribute('disabled')) return;

      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.click();
      }

      if (event.key === 'Enter') {
        this.click();
      }
    });

    // Listen for wishlist updates
    document.addEventListener(WISHLIST_UPDATED_EVENT, () => {
      this.updateUI();
    });
  }

  /**
   * Update button UI based on wishlist state
   */
  updateUI() {
    const iconEmpty = this.querySelector('.wishlist-icon-empty');
    const iconFilled = this.querySelector('.wishlist-icon-filled');

    if (!iconEmpty || !iconFilled || !this.wishlist) {
      console.warn('[ARN Wishlist Button] Missing icons or wishlist component');
      return;
    }

    const isInWishlist = this.wishlist.isItemInWishlist({
      dataset: {
        productId: this.productId,
        variantId: this.variantId
      }
    });

    // [debug] console.log('[ARN Wishlist Button] UI update - In wishlist:', isInWishlist);

    // Toggle icon visibility
    if (isInWishlist) {
      iconEmpty.classList.add('tw-hidden');
      iconFilled.classList.remove('tw-hidden');
      this.classList.add('wishlist-active');
      this.setAttribute('aria-pressed', 'true');
      this.buttonEl.setAttribute('data-element-id', 'Prod_RemoveFromWishList');
    } else {
      iconEmpty.classList.remove('tw-hidden');
      iconFilled.classList.add('tw-hidden');
      this.classList.remove('wishlist-active');
      this.setAttribute('aria-pressed', 'false');
      this.buttonEl.setAttribute('data-element-id', 'Prod_AddToWishList');
    }
  }
}

// Register custom element
if (customElements.get('arn-wishlist-toggle-button') === undefined) {
  customElements.define('arn-wishlist-toggle-button', ArnWishlistToggleButton);
}

/**
 * Load product-form script dynamically if not already loaded
 * This allows us to reuse the PDP's add to cart functionality
 * @returns {Promise} Resolves when product-form is ready
 */
function loadProductFormScript() {
  return new Promise((resolve) => {
    // Check if product-form custom element is already defined
    if (customElements.get('product-form')) {
      // [debug] console.log('[ARN Wishlist] product-form already loaded');
      resolve();
      return;
    }

    // Create script element and load product-form.js
    const script = document.createElement('script');
    script.src = window.Shopify.routes.root + 'cdn/shop/t/12/assets/product-form.js';
    script.defer = true;
    script.onload = () => {
      // [debug] console.log('[ARN Wishlist] product-form.js loaded successfully');
      resolve();
    };
    script.onerror = () => {
      console.error('[ARN Wishlist] Failed to load product-form.js');
      resolve(); // Resolve anyway to prevent hanging
    };
    document.head.appendChild(script);
  });
}

/**
 * Initialize add to cart functionality for wishlist items
 * This function sets up the add to cart buttons to work like PDP
 * by leveraging the existing product-form custom element
 */
async function initWishlistAddToCart() {
  // [debug] console.log('[ARN Wishlist] Initializing add to cart functionality...');

  // First, ensure product-form script is loaded
  await loadProductFormScript();

  // Wait a bit for custom elements to be fully registered
  await new Promise(resolve => setTimeout(resolve, 100));

  // Get all wishlist product forms
  const wishlistForms = document.querySelectorAll('.wishlist-add-to-cart-form');
  
  // [debug] console.log('[ARN Wishlist] Found', wishlistForms.length, 'add to cart forms');

  if (wishlistForms.length === 0) {
    console.warn('[ARN Wishlist] No add to cart forms found');
    return;
  }

  // Set up form submit handlers to update timestamp
  wishlistForms.forEach(form => {
    // Add submit event listener to update timestamp before submission
    form.addEventListener('submit', (e) => {
      const timestampInput = form.querySelector('input[name="properties[_cart_added_at]"]');
      if (timestampInput) {
        // Set current timestamp in milliseconds
        timestampInput.value = Date.now();
        // [debug] console.log('[ARN Wishlist] Timestamp set for form submission');
      }
    });
  });

  // [debug] console.log('[ARN Wishlist] Add to cart initialization complete');
}

/**
 * Smart Wishlist Callback Functions
 * These are called by the Smart Wishlist app
 */
function SWCallbackExpressWishlistCollectionItemAdded() {
  console.log('[Smart Wishlist Callback] Item added (guest, collection)');
}

function SWCallbackExpressWishlistCollectionItemRemoved() {
  console.log('[Smart Wishlist Callback] Item removed (guest, collection)');
}

function SWCallbackCustomerWishlistCollectionItemAdded() {
  console.log('[Smart Wishlist Callback] Item added (customer, collection)');
}

function SWCallbackCustomerWishlistCollectionItemRemoved() {
  console.log('[Smart Wishlist Callback] Item removed (customer, collection)');
}

function SWCallbackExpressWishlistProductItemAdded() {
  console.log('[Smart Wishlist Callback] Item added (guest, product)');
}

function SWCallbackExpressWishlistProductItemRemoved() {
  console.log('[Smart Wishlist Callback] Item removed (guest, product)');
}

function SWCallbackCustomerWishlistProductItemAdded() {
  console.log('[Smart Wishlist Callback] Item added (customer, product)');
}

function SWCallbackCustomerWishlistProductItemRemoved() {
  console.log('[Smart Wishlist Callback] Item removed (customer, product)');
}